import type * as React from "react";
import { Wrench } from "lucide-react";
import type { I18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ActionFieldValue } from "../ActionFieldEditor";
import { SkeletonText } from "../../primitives/skeleton";
import {
  VendorPreviewField,
  VendorPreviewShell,
} from "./VendorPreviewShell";

/**
 * GenericProposalPreview — fallback preview surface for action_types
 * that don't have a dedicated vendor card.
 *
 * Of the 25 action types Ando v2 supports, 6 ship a custom preview
 * (Email, Calendar, FiC Create Invoice, FiC Credit Note, FiC Payment,
 * PEC, Acube SDI, Document Edit/Create). The remaining 19 (task_create,
 * automation_trigger, salesforce_*, hubspot_*, sap_*, fic_payment,
 * acube_register_cassetto/archive_conservazione/send_peppol,
 * infocert_pec_send, fic_schedule_invoice_email, …) cascade to this
 * generic card which:
 *
 *   - If ``proposedFields`` is non-empty: pretty-prints the dict as a
 *     2-column ``<dl>`` (key prettified from snake_case → "Snake Case")
 *     inside a ``VendorPreviewShell``. The user sees what the agent
 *     intends, even before the canonical schema lands.
 *   - If ``proposedFields`` is empty (BE hasn't shipped the wire yet
 *     or schema is in-flight): renders a 4-row ``<SkeletonText>``
 *     inside the same shell, so the focused canvas isn't empty for
 *     the 100-300ms between proposal arrival and field hydration.
 */

function prettify(key: string): string {
  // snake_case → "Snake Case"; strip leading "field_" prefix some
  // vendors ship; preserve ALL_CAPS acronyms (P.IVA, IBAN, RIBA).
  const cleaned = key.replace(/^field_/, "").replace(/_/g, " ");
  return cleaned.replace(/\b\w/g, (c) => c.toUpperCase());
}

function renderValue(value: ActionFieldValue, i18n: I18n): React.ReactNode {
  if (value === null || value === undefined) return null;
  if (typeof value === "boolean")
    return value ? i18n._(msg`Sì`) : i18n._(msg`No`);
  if (typeof value === "number") {
    return <span className="font-mono tabular-nums">{value}</span>;
  }
  // Arrays (attendees, tags, …): joined explicitly — React renders raw array
  // children CONCATENATED without any separator (observed live Aug 26, 2026:
  // "a@x.comb@y.com" in the calendar proposal preview).
  if (Array.isArray(value)) {
    return value.map((v) => String(v)).join(", ");
  }
  // Multi-line strings → preserve linebreaks.
  if (typeof value === "string" && value.includes("\n")) {
    return (
      <span className="whitespace-pre-wrap text-[13px] leading-relaxed">
        {value}
      </span>
    );
  }
  return value;
}

export interface GenericProposalPreviewProps {
  readonly title: string;
  readonly proposedFields: Readonly<Record<string, ActionFieldValue>>;
  readonly executedFields?: Readonly<Record<string, ActionFieldValue>>;
  /** Action-type literal — surfaced in caption as a short hint. */
  readonly actionType?: string;
}

export const GenericProposalPreview: React.FC<GenericProposalPreviewProps> = ({
  title,
  proposedFields,
  executedFields,
  actionType,
}) => {
  const { i18n } = useLingui();
  const merged = { ...proposedFields, ...(executedFields ?? {}) };
  const entries = Object.entries(merged).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );

  // Skeleton state — BE hasn't shipped the fields yet.
  if (entries.length === 0) {
    return (
      <VendorPreviewShell
        icon={<Wrench className="h-4 w-4" />}
        title={title}
        caption={
          actionType
            ? i18n._(msg`Proposta · ${actionType}`)
            : i18n._(msg`Proposta`)
        }
        footnote={i18n._(
          msg`L'agente sta preparando i dettagli — la card si popolerà appena la proposta è completa.`,
        )}
      >
        <VendorPreviewField
          label={i18n._(msg`Caricamento`)}
          wide
          value={<SkeletonText lines={4} />}
        />
      </VendorPreviewShell>
    );
  }

  return (
    <VendorPreviewShell
      icon={<Wrench className="h-4 w-4" />}
      title={title}
      caption={
        actionType
          ? i18n._(msg`Proposta · ${actionType}`)
          : i18n._(msg`Proposta`)
      }
    >
      {entries.map(([key, value]) => (
        <VendorPreviewField
          key={key}
          label={prettify(key)}
          value={renderValue(value, i18n)}
          // ``content`` / ``body_text`` / ``description`` are long — span
          // both columns so they don't truncate mid-paragraph.
          wide={
            key === "content" ||
            key === "body_text" ||
            key === "body" ||
            key === "description" ||
            key === "notes"
          }
          hideEmpty
        />
      ))}
    </VendorPreviewShell>
  );
};
GenericProposalPreview.displayName = "GenericProposalPreview";
